/**
 * Controlla se lo storage è già persistente.
 * @returns Promise che risolve a `true` se l'origine corrente usa uno storage persistente, `false` in caso
 * contrario o se l'API non è disponibile nel browser.
 */
const isStoragePersisted = async (): Promise<boolean> => {
  if (!('storage' in navigator) || !('persisted' in navigator.storage)) {
    return false;
  }

  return await navigator.storage.persisted();
};

/**
 * Prova a rendere lo storage persistente.
 * @returns Promise che risolve a `true` se la persistenza va a buon fine, `false` in caso contrario o se
 * l'API non è disponibile nel browser.
 */
const persist = async (): Promise<boolean> => {
  if (!('storage' in navigator) || !('persist' in navigator.storage)) {
    return false;
  }

  return await navigator.storage.persist();
};

const checkPersistentPermission = async (): Promise<'persisted' | 'prompt' | 'never'> => {
  if (!('permissions' in navigator) || !('query' in navigator.permissions)) {
    return 'prompt';
  }

  const permission = await navigator.permissions.query({ name: 'persistent-storage' });

  if (permission.state === 'prompt') {
    return 'prompt';
  }

  if (permission.state === 'granted') {
    const persisted = await navigator.storage.persist();
    if (!persisted) {
      console.error('Failed to persist');
      return 'never';
    }
    return 'persisted';
  }

  return 'never';
};

/**
 * Prova a rendere lo storage persistente senza mai mostrare il prompt all'utente.
 * @returns Promise che risolve a `never` se la persistenza non è mai possibile, e il chiamante non deve
 * chiedere il permesso all'utente; a `prompt` se la persistenza sarebbe possibile chiedendo prima il
 * permesso; a `persisted` se questa chiamata ha reso lo storage persistente in silenzio, oppure se lo era
 * già.
 */
export const tryPersistWithoutPromtingUser = async (): Promise<
  'never' | 'persisted' | 'prompt' | 'persisted-storage'
> => {
  const persisted = await isStoragePersisted();
  if (persisted) {
    return 'persisted';
  }

  return checkPersistentPermission();
};
